/**
 * Client for the backend partner API (/api/partner). Available only when the
 * app is deployed with the caching proxy (VITE_API_BASE); on pure-RPC builds
 * the dashboard degrades gracefully (links still work, extended stats hidden).
 */
import { API_BASE, USE_PROXY } from '../ton/config';

export const PARTNER_API_AVAILABLE = USE_PROXY;

export type PartnerEvent =
  | 'start'
  | 'app_open'
  | 'wallet_auth'
  | 'ticket_purchase'
  | 'first_referral'
  | 'team_start'
  | 'intake_complete';

export const PARTNER_EVENTS: PartnerEvent[] = [
  'start',
  'app_open',
  'wallet_auth',
  'ticket_purchase',
  'first_referral',
  'team_start',
  'intake_complete',
];

export interface EventConfig {
  enabled: boolean;
  url: string;
}
export type PartnerEvents = Partial<Record<PartnerEvent, EventConfig>>;

export interface SubStat {
  subId: string;
  visitors: number;
  events: Record<string, { total: number; sent: number }>;
  /** On-chain referred revenue volume attributed to this source in the window. */
  revenueNano: string;
  /** Partner's L1 commission on that volume (nanoGRAM); absent on old backends. */
  commissionNano?: string;
  /** On-chain referred revenue events attributed to this source in the window. */
  purchases: number;
  /** Manual ad spend the partner entered for this source (USD). */
  costUsd: number;
}

export interface SeriesPoint {
  date: string; // YYYY-MM-DD (UTC)
  signups: number;
  purchases: number;
  volumeNano: string;
  /** Partner's L1 commission on the day's volume; absent on old backends. */
  commissionNano?: string;
}

export interface PartnerSource {
  id: number;
  label: string;
  subId: string;
  /** Per-source postback config; empty until the partner configures it. */
  events: PartnerEvents;
  /** When true the source inherits main's postbacks instead of its own. */
  useMain: boolean;
}

export interface PartnerOverview {
  registered: boolean;
  key?: string;
  days?: number;
  /** Traffic source (sub_id) the payload is filtered to; '' = all sources. */
  source?: string;
  referralsCount?: number;
  earningsNano?: string;
  campaign?: { active: boolean; events: PartnerEvents };
  funnel?: {
    invited: number;
    appOpen: number;
    walletAuth: number;
    ticketPurchase: number;
    secondGen: number;
  } | null;
  /** Window bounds actually applied by the backend (UTC, inclusive). */
  from?: string;
  to?: string;
  /** Live GRAM/USD price for USD conversions; null when unavailable. */
  tonUsdRate?: number | null;
  /** L1 referrer commission share of a buy, in percent (mirrors on-chain). */
  referralSharePercent?: number;
  onchain?: PeriodTotals;
  /** Same-length window immediately before this one, for exact deltas. */
  prev?: PeriodTotals;
  subStats?: SubStat[];
  series?: SeriesPoint[];
  /** Daily trend for the previous, equal-length window (for chart overlay). */
  prevSeries?: SeriesPoint[];
  sources?: PartnerSource[];
}

export interface PeriodTotals {
  signups: number;
  purchases: number;
  volumeNano: string;
  /** Partner's L1 commission on the window's volume; absent on old backends. */
  commissionNano?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/partner${path}`, init);
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function fetchProofPayload(): Promise<string> {
  const { payload } = await request<{ payload: string }>('/auth/payload', { method: 'POST' });
  return payload;
}

export interface ProofSubmission {
  address: string;
  publicKey: string;
  walletStateInit: string;
  proof: {
    timestamp: number;
    domain: { lengthBytes: number; value: string };
    payload: string;
    signature: string;
  };
}

export async function verifyProof(body: ProofSubmission, remember = false): Promise<string> {
  const { token } = await request<{ token: string }>('/auth/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, remember }),
  });
  return token;
}

const authHeaders = (token: string): HeadersInit => ({ Authorization: `Bearer ${token}` });

/**
 * Ends the server-side session for this wallet: bumps its revocation epoch so
 * every token issued to it (including this one) stops working immediately.
 * Best-effort — the caller clears the local token regardless.
 */
export async function logoutPartner(token: string): Promise<void> {
  try {
    await request<{ ok: boolean }>('/logout', {
      method: 'POST',
      headers: authHeaders(token),
      keepalive: true,
    });
  } catch {
    /* best-effort: local token is cleared by the caller anyway */
  }
}

export interface OverviewRange {
  days?: number;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  /** Optional traffic source (sub_id) to scope the whole overview to. */
  source?: string;
}

export async function fetchOverview(
  token: string,
  range?: OverviewRange,
): Promise<PartnerOverview> {
  const params = new URLSearchParams();
  if (range?.from && range?.to) {
    params.set('from', range.from);
    params.set('to', range.to);
  } else if (range?.days) {
    params.set('days', String(range.days));
  }
  if (range?.source) params.set('source', range.source);
  const q = params.toString();
  return request<PartnerOverview>(`/overview${q ? `?${q}` : ''}`, { headers: authHeaders(token) });
}

/** One dated ad-spend entry (USD) a partner recorded for a source. */
export interface SpendEntry {
  id: number;
  subId: string;
  fromDate: string; // YYYY-MM-DD
  toDate: string; // YYYY-MM-DD
  costUsd: number;
}

/** All dated spend entries the partner has recorded (newest first). */
export async function fetchSpend(token: string): Promise<SpendEntry[]> {
  const { entries } = await request<{ entries: SpendEntry[] }>('/sources/spend', {
    headers: authHeaders(token),
  });
  return entries;
}

/** Records one dated ad-spend entry (USD) for a source; entries accumulate. */
export async function addSpend(
  token: string,
  entry: { subId: string; fromDate: string; toDate: string; costUsd: number },
): Promise<void> {
  await request<{ ok: boolean }>('/sources/spend', {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
}

/** Deletes one of the partner's spend entries by id. */
export async function deleteSpend(token: string, id: number): Promise<void> {
  await request<{ ok: boolean }>(`/sources/spend/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export async function createSource(
  token: string,
  label: string,
  subId: string,
): Promise<PartnerSource> {
  const { source } = await request<{ source: PartnerSource }>('/sources', {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ label, subId }),
  });
  return source;
}

export async function deleteSource(token: string, id: number): Promise<void> {
  await request<{ ok: boolean }>(`/sources/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export interface CommissionRow {
  txHash: string;
  utime: number; // unix seconds
  buyer: string | null;
  amountNano: string;
}

export async function fetchCommissions(token: string, limit = 100): Promise<CommissionRow[]> {
  const { commissions } = await request<{ commissions: CommissionRow[] }>(
    `/commissions?limit=${limit}`,
    { headers: authHeaders(token) },
  );
  return commissions;
}

export interface ActivityRow {
  event: string;
  subId: string;
  status: number | null;
  /** A postback URL was configured and delivery was attempted for this event. */
  fired: boolean;
  at: string; // ISO timestamp
}

export async function fetchActivity(
  token: string,
  limit = 50,
  source?: string,
): Promise<ActivityRow[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (source) params.set('source', source);
  const { activity } = await request<{ activity: ActivityRow[] }>(`/activity?${params}`, {
    headers: authHeaders(token),
  });
  return activity;
}

export async function testPostback(
  token: string,
  url: string,
): Promise<{ status: number | null; error?: string }> {
  return request<{ status: number | null; error?: string }>('/postbacks/test', {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
}

export interface NetworkLevel {
  level: number;
  invited: number;
  partners: number;
}

export async function fetchNetwork(token: string): Promise<NetworkLevel[] | null> {
  const { network } = await request<{ network: NetworkLevel[] | null }>('/network', {
    headers: authHeaders(token),
  });
  return network;
}

export interface CohortRow {
  cohort: string; // ISO week, e.g. "2026-W07"
  buyers: number;
  purchases: number;
  volumeNano: string;
}

export async function fetchCohorts(token: string, limit = 26): Promise<CohortRow[]> {
  const { cohorts } = await request<{ cohorts: CohortRow[] }>(`/cohorts?limit=${limit}`, {
    headers: authHeaders(token),
  });
  return cohorts;
}

export async function savePostbacks(
  token: string,
  events: PartnerEvents,
): Promise<{ active: boolean; events: PartnerEvents }> {
  const { campaign } = await request<{ campaign: { active: boolean; events: PartnerEvents } }>(
    '/postbacks',
    {
      method: 'PUT',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
    },
  );
  return campaign;
}

/** Saves a named source's own postback config (or flags it to inherit main's). */
export async function saveSourcePostbacks(
  token: string,
  id: number,
  events: PartnerEvents,
  useMain: boolean,
): Promise<PartnerSource> {
  const { source } = await request<{ source: PartnerSource }>(`/sources/${id}/postbacks`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ events, useMain }),
  });
  return source;
}

// ── API keys ──────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string;
  label: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export async function fetchApiKeys(token: string): Promise<ApiKey[]> {
  const { keys } = await request<{ keys: ApiKey[] }>('/keys', { headers: authHeaders(token) });
  return keys;
}

/** Returns the full plaintext key — shown to the partner exactly once. */
export async function createApiKey(
  token: string,
  label: string,
): Promise<{ id: string; apiKey: string; keyPrefix: string }> {
  return request<{ id: string; apiKey: string; keyPrefix: string }>('/keys', {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  });
}

export async function revokeApiKey(token: string, id: string): Promise<void> {
  await request<{ ok: boolean }>(`/keys/${id}`, { method: 'DELETE', headers: authHeaders(token) });
}

// ── Webhooks ────────────────────────────────────────────────────────────────

export interface Webhook {
  id: string;
  url: string;
  secret: string;
  events: PartnerEvent[];
  active: boolean;
  createdAt: string;
}

export interface WebhookDelivery {
  event: string;
  status: number | null;
  error: string | null;
  createdAt: string;
}

export async function fetchWebhooks(token: string): Promise<Webhook[]> {
  const { webhooks } = await request<{ webhooks: Webhook[] }>('/webhooks', {
    headers: authHeaders(token),
  });
  return webhooks;
}

export async function createWebhook(
  token: string,
  url: string,
  events: PartnerEvent[],
): Promise<Webhook> {
  const { webhook } = await request<{ webhook: Webhook }>('/webhooks', {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, events }),
  });
  return webhook;
}

export async function deleteWebhook(token: string, id: string): Promise<void> {
  await request<{ ok: boolean }>(`/webhooks/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export async function testWebhook(
  token: string,
  id: string,
): Promise<{ status: number | null; error?: string }> {
  return request<{ status: number | null; error?: string }>(`/webhooks/${id}/test`, {
    method: 'POST',
    headers: authHeaders(token),
  });
}

export async function fetchWebhookDeliveries(
  token: string,
  id: string,
  limit = 50,
): Promise<WebhookDelivery[]> {
  const { deliveries } = await request<{ deliveries: WebhookDelivery[] }>(
    `/webhooks/${id}/deliveries?limit=${limit}`,
    { headers: authHeaders(token) },
  );
  return deliveries;
}
